import { useMemo, useState } from "react";
import { api, ApiError } from "../api/client";
import { useQuery } from "../api/useQuery";
import { LazyMap } from "../components/LazyMap";
import type { MapMarker } from "../components/MapView";
import { fmtDateTime } from "../components/format";
import type { GeoTerritory, Tag, TagEvent, TagStatus, UnknownTag } from "../api/types";

const STATUS_NAME: Record<TagStatus, string> = {
  prepared: "Подготовлена",
  active: "Действует",
  damaged: "Повреждена",
  lost: "Утрачена",
  replaced: "Заменена",
  blocked: "Заблокирована",
};

const STATUS_TONE: Record<TagStatus, MapMarker["tone"]> = {
  prepared: "neutral", active: "ok", damaged: "danger", lost: "danger", replaced: "neutral", blocked: "danger",
};

const FILTERS: [string, string, TagStatus[] | null][] = [
  ["all", "Все", null],
  ["active", "Действуют", ["active"]],
  ["prepared", "Подготовлены", ["prepared"]],
  ["attention", "Требуют внимания", ["damaged", "lost", "blocked"]],
];

type Draft = {
  territory_id: string;
  route_id: string;
  point_name: string;
  lat: number | null;
  lon: number | null;
  radius_m: number;
  uid_hex: string;
  status: TagStatus | "";
};

/**
 * Контрольные точки. Точку можно спланировать до монтажа — кликом
 * по карте, без UID. При установке метки UID вписывают вручную или берут
 * из меток, которые инспектор уже считал телефоном.
 */
export function TagsPage() {
  const tags = useQuery<{ items: Tag[] }>("/api/v1/tags");
  const geo = useQuery<{ items: GeoTerritory[] }>("/api/v1/geo");
  const unknown = useQuery<{ items: UnknownTag[] }>("/api/v1/tags/unknown");

  const [filter, setFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const items = tags.data?.items ?? [];
  const territories = geo.data?.items ?? [];
  const roles = FILTERS.find(([id]) => id === filter)?.[2];
  const visible = items.filter((t) => !roles || roles.includes(t.status));
  const groups = useMemo(() => {
    const out = new Map<string, Tag[]>();
    for (const t of visible) {
      const key = `${t.territory_name} · ${t.route_name ?? "без маршрута"}`;
      out.set(key, [...(out.get(key) ?? []), t]);
    }
    return [...out.entries()];
  }, [visible]);

  const selected = items.find((t) => t.id === selectedId) ?? null;
  const count = (s: TagStatus[]) => items.filter((t) => s.includes(t.status)).length;
  const reload = () => { tags.reload(); unknown.reload(); };

  return (
    <>
      <div className="page-head">
        <div>
          <h2>Контрольные точки</h2>
          {tags.data && <p className="muted small">
            {count(["active"])} действуют · {count(["prepared"])} подготовлено
            {count(["damaged", "lost", "blocked"]) > 0 && ` · ${count(["damaged", "lost", "blocked"])} требуют внимания`}
          </p>}
        </div>
        {territories.length > 0 && (
          <button onClick={() => { setCreating(true); setSelectedId(null); }}>Новая точка</button>
        )}
      </div>

      <div className="filters">
        <div className="segmented" role="group" aria-label="Статус">
          {FILTERS.map(([id, title]) => (
            <button key={id} aria-pressed={filter === id} onClick={() => setFilter(id)}>{title}</button>
          ))}
        </div>
      </div>

      {tags.error && <div className="error" role="alert">{tags.error}</div>}

      <div className="split">
        <div className="split-list" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {tags.data && visible.length === 0 && (
            <div className="card empty">
              {items.length === 0
                ? "Точек пока нет. Нажмите «Новая точка» и поставьте её кликом по карте — метку можно привязать позже."
                : "Нет точек с таким статусом"}
            </div>
          )}
          {groups.map(([title, list]) => (
            <div key={title}>
              <div className="section-title">{title}</div>
              <div className="list">
                {list.map((t) => (
                  <button key={t.id} className={`item${t.id === selectedId && !creating ? " selected" : ""}`}
                    onClick={() => { setSelectedId(t.id); setCreating(false); }}>
                    <span className="item-body">
                      <span className="item-head">
                        <span className="item-title">{t.point_name}</span>
                        <span className={`badge${t.status === "active" ? " ok" : ["damaged", "lost", "blocked"].includes(t.status) ? " danger" : ""}`}>
                          {STATUS_NAME[t.status]}
                        </span>
                      </span>
                      <span className="item-text">
                        {t.uid_hex ?? "метка не установлена"} · радиус {t.radius_m} м
                        {t.last_read_at && ` · считана ${fmtDateTime(t.last_read_at)}`}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="split-detail">
          {creating && territories[0] && (
            <TagEditor key="new" tag={null} all={items} territories={territories}
              unknown={unknown.data?.items ?? []}
              onSaved={(id) => { reload(); setCreating(false); setSelectedId(id); }}
              onCancel={() => setCreating(false)} />
          )}
          {!creating && selected && (
            <TagEditor key={selected.id} tag={selected} all={items} territories={territories}
              unknown={unknown.data?.items ?? []} onSaved={reload} />
          )}
          {!creating && !selected && <div className="card empty">Выберите точку или создайте новую</div>}
        </div>
      </div>
    </>
  );
}

function TagEditor({ tag, all, territories, unknown, onSaved, onCancel }: {
  tag: Tag | null;
  all: Tag[];
  territories: GeoTerritory[];
  unknown: UnknownTag[];
  onSaved: (id: string) => void;
  onCancel?: () => void;
}) {
  const [d, setD] = useState<Draft>(() => tag
    ? { territory_id: tag.territory_id, route_id: tag.route_id ?? "", point_name: tag.point_name,
        lat: tag.lat, lon: tag.lon, radius_m: tag.radius_m, uid_hex: tag.uid_hex ?? "", status: tag.status }
    : { territory_id: territories[0]!.id, route_id: "", point_name: "", lat: null, lon: null,
        radius_m: 100, uid_hex: "", status: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const history = useQuery<{ items: TagEvent[] }>(tag ? `/api/v1/tags/${tag.id}/history` : null);

  const territory = territories.find((t) => t.id === d.territory_id);
  const route = territory?.routes.find((r) => r.id === d.route_id);
  const markers = useMemo<MapMarker[]>(() => [
    ...all.filter((t) => t.territory_id === d.territory_id && t.id !== tag?.id).map((t) => ({
      lat: t.lat, lon: t.lon, label: `${t.point_name} · ${STATUS_NAME[t.status]}`, tone: STATUS_TONE[t.status],
    })),
    ...(d.lat !== null && d.lon !== null
      ? [{ lat: d.lat, lon: d.lon, label: d.point_name || "Новая точка", tone: "warn" as const }]
      : []),
  ], [all, d.territory_id, d.lat, d.lon, d.point_name, tag?.id]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const body = {
      territory_id: d.territory_id, route_id: d.route_id || null, point_name: d.point_name,
      lat: d.lat ?? 0, lon: d.lon ?? 0, radius_m: d.radius_m, uid_hex: d.uid_hex || null, status: d.status,
    };
    try {
      if (tag) {
        await api.put(`/api/v1/tags/${tag.id}`, body);
        onSaved(tag.id);
        history.reload();
      } else {
        onSaved((await api.post<{ id: string }>("/api/v1/tags", body)).id);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось сохранить");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card" aria-label="Контрольная точка">
      <div className="muted small">{territory?.name}</div>
      <h3 style={{ fontSize: 22, marginTop: 2 }}>{tag ? tag.point_name : "Новая точка"}</h3>

      <div style={{ borderRadius: 12, overflow: "hidden", margin: "12px 0 6px" }}>
        <LazyMap
          polygons={territory?.boundary ? [territory.boundary] : []}
          routes={route?.path ? [route.path] : (territory?.routes ?? []).filter((r) => r.is_active && r.path).map((r) => r.path)}
          markers={markers}
          height={320}
          fitKey={`${d.territory_id}:${tag?.id ?? "new"}`}
          onPick={(lat, lon) => setD({ ...d, lat: round(lat), lon: round(lon) })}
        />
      </div>
      <p className="muted small" style={{ margin: "0 0 14px" }}>
        {d.lat !== null && d.lon !== null
          ? `Точка: ${d.lat}, ${d.lon} — кликните по карте, чтобы перенести`
          : "Кликните по карте, чтобы поставить точку"}
      </p>

      <form onSubmit={(e) => void save(e)} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {error && <div className="error" role="alert">{error}</div>}
        {!tag && territories.length > 1 && (
          <label className="field">Территория
            <select value={d.territory_id} onChange={(e) => setD({ ...d, territory_id: e.target.value, route_id: "" })}>
              {territories.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>
        )}
        <label className="field">Название
          <input required value={d.point_name} placeholder="КТ-3 · Родник" onChange={(e) => setD({ ...d, point_name: e.target.value })} />
        </label>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
          <label className="field">Маршрут
            <select value={d.route_id} onChange={(e) => setD({ ...d, route_id: e.target.value })}>
              <option value="">Без маршрута</option>
              {(territory?.routes ?? []).filter((r) => r.is_active).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </label>
          <label className="field">Радиус, м
            <input type="number" min={10} max={1000} step={10} value={d.radius_m}
              onChange={(e) => setD({ ...d, radius_m: Number(e.target.value) })} />
          </label>
        </div>
        <label className="field">UID метки {tag?.uid_hex ? "" : "(когда метка установлена)"}
          <input value={d.uid_hex} placeholder="04:A1:B2:C3:D4:E5:80" autoComplete="off"
            onChange={(e) => setD({ ...d, uid_hex: e.target.value })} />
        </label>
        {unknown.length > 0 && !d.uid_hex && (
          <div>
            <div className="section-title">Считаны телефоном, но не в реестре</div>
            <div className="list" style={{ background: "var(--bg)" }}>
              {unknown.slice(0, 5).map((u) => (
                <div className="row" key={u.uid_hex}>
                  <span>
                    <span className="mono">{u.uid_hex}</span>
                    <span className="row-sub">{u.inspector} · {fmtDateTime(u.last_read_at)} · считываний: {u.reads}</span>
                  </span>
                  <button type="button" className="secondary" onClick={() => setD({
                    ...d, uid_hex: u.uid_hex,
                    lat: d.lat ?? (u.lat !== null ? round(u.lat) : null), lon: d.lon ?? (u.lon !== null ? round(u.lon) : null),
                  })}>Привязать</button>
                </div>
              ))}
            </div>
          </div>
        )}
        <label className="field">Статус
          <select value={d.status} onChange={(e) => setD({ ...d, status: e.target.value as TagStatus | "" })}>
            {!tag && <option value="">{d.uid_hex ? "Действует" : "Подготовлена"} — по наличию UID</option>}
            {(Object.keys(STATUS_NAME) as TagStatus[]).map((s) => <option key={s} value={s}>{STATUS_NAME[s]}</option>)}
          </select>
        </label>
        <div className="filters" style={{ margin: 0 }}>
          <button type="submit" disabled={busy || d.lat === null}>{tag ? "Сохранить" : "Создать точку"}</button>
          {onCancel && <button type="button" className="secondary" onClick={onCancel}>Отмена</button>}
        </div>
      </form>

      {tag && history.data && history.data.items.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div className="section-title">История</div>
          <div className="list" style={{ background: "var(--bg)" }}>
            {history.data.items.map((e, i) => (
              <div className="row" key={i}>
                <span>{describe(e)}<span className="row-sub">{e.actor ?? "система"}</span></span>
                <span className="muted small">{fmtDateTime(e.at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

/** Что изменилось — словами, а не JSON. */
function describe(e: TagEvent): string {
  if (e.action === "tag.create") return "Точка создана";
  const o = (e.detail?.old ?? {}) as Record<string, unknown>;
  const n = (e.detail?.new ?? {}) as Record<string, unknown>;
  const parts: string[] = [];
  if (o.status !== n.status) parts.push(`статус: ${STATUS_NAME[o.status as TagStatus] ?? o.status} → ${STATUS_NAME[n.status as TagStatus] ?? n.status}`);
  if (o.uid_hex !== n.uid_hex) parts.push(n.uid_hex ? `метка ${n.uid_hex}` : "метка снята");
  if (o.lat !== n.lat || o.lon !== n.lon) parts.push("перенесена");
  if (o.radius_m !== n.radius_m) parts.push(`радиус ${n.radius_m} м`);
  if (o.point_name !== n.point_name) parts.push(`переименована в «${n.point_name}»`);
  if (o.route_id !== n.route_id) parts.push("сменён маршрут");
  return parts.length ? parts.join(", ") : "Изменена";
}

const round = (v: number) => Math.round(v * 1e6) / 1e6;
